import React from 'react';
import { ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';

/**
 * Inline deliverability shield based on `lead.email_verification` (set by Hunter).
 * Renders nothing when no verification exists.
 *
 * Variants:
 *   icon-only (default, compact)  — for tight rows / cards
 *   chip                          — small pill with status text
 */
export function HunterDeliverabilityShield({ verification, variant = 'icon-only', className = '' }) {
  if (!verification) return null;
  const status = String(verification.status || verification.result || '').toLowerCase();
  if (!status) return null;

  let Icon = ShieldQuestion;
  let cls = 'text-amber-600';
  let chipCls = 'bg-amber-50 text-amber-700 border-amber-200';
  let label = status;

  if (status === 'valid' || status === 'deliverable') {
    Icon = ShieldCheck;
    cls = 'text-emerald-600';
    chipCls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    label = 'deliverable';
  } else if (status === 'invalid' || status === 'undeliverable') {
    Icon = ShieldAlert;
    cls = 'text-red-600';
    chipCls = 'bg-red-50 text-red-700 border-red-200';
    label = 'undeliverable';
  }

  const score = typeof verification.score === 'number' ? verification.score : null;
  const tooltip = `Hunter: ${label}${score !== null ? ` · ${score}/100` : ''}${verification.checked_at ? ` · ${new Date(verification.checked_at).toLocaleDateString()}` : ''}`;

  if (variant === 'chip') {
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.15em] font-medium rounded-sm border ${chipCls} ${className}`}
        title={tooltip}
        data-testid="hunter-shield-chip"
      >
        <Icon className="w-3 h-3" />
        {label}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center ${className}`}
      title={tooltip}
      data-testid="hunter-shield-icon"
      aria-label={tooltip}
    >
      <Icon className={`w-3.5 h-3.5 ${cls}`} />
    </span>
  );
}
